import { useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { configService } from "@shared/src/services/config.service";
import { useAuthStore } from "@shared/src/stores/useAuthStore";
import { AccountPaymentConfig } from "@sdk/shared/types/config.types";
import { USER_PAYMENT_CONFIGS_QUERY_KEY } from "@shared/src/lib/queryKeys";

export interface UseUserPaymentConfigsResult {
  userPaymentConfigs: AccountPaymentConfig[];
  refetch: UseQueryResult<AccountPaymentConfig[], unknown>["refetch"];
  isLoading: boolean;
  updatePaymentConfigs: (paymentConfigsToAdd: AccountPaymentConfig[]) => void;
  deletePaymentConfig: (paymentConfigId: number) => void;
}

export function useUserPaymentConfigs(enabled = true): UseUserPaymentConfigsResult {
  const { activeAccount } = useAuthStore();
  const client = useQueryClient();

  const {
    data: userPaymentConfigs = [],
    refetch,
    isLoading,
  } = useQuery({
    queryKey: USER_PAYMENT_CONFIGS_QUERY_KEY(activeAccount?.address || ""),
    queryFn: async () => {
      const { data } = await configService.getAccountPaymentConfigs();
      return data || [];
    },
    enabled: enabled && !!activeAccount?.address,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 30000, // Consider data stale after 30 seconds
  });

  const updatePaymentConfigs = (paymentConfigsToAdd: AccountPaymentConfig[]) => {
    client.setQueryData(
      USER_PAYMENT_CONFIGS_QUERY_KEY(activeAccount?.address || ""),
      (oldPaymentConfigs: AccountPaymentConfig[]) => {
        return oldPaymentConfigs.concat(paymentConfigsToAdd);
      },
    );
    return;
  };

  const deletePaymentConfig = (paymentConfigId: number) => {
    client.setQueryData(
      USER_PAYMENT_CONFIGS_QUERY_KEY(activeAccount?.address || ""),
      (oldPaymentConfigs: AccountPaymentConfig[]) => {
        return oldPaymentConfigs.filter(config => config.id !== paymentConfigId);
      },
    );
    return;
  };

  return { userPaymentConfigs, refetch, isLoading, updatePaymentConfigs, deletePaymentConfig };
}
